import { useState, useCallback, useRef, useEffect } from 'react';
import styles from './ProducerSchedulePage.module.css';
import ProducerContentContainer from '../layout/ProducerContentContainer';
import CalendarGrid from '../../../shared/schedule/CalendarGrid';
import UpcomingPanel from '../../../shared/schedule/UpcomingPanel';
import AddEventModal from '../../../shared/schedule/AddEventModal';
import EventDetailModal from '../../../shared/schedule/EventDetailModal';
import DayEventsModal from '../../../shared/schedule/DayEventsModal';
import { getProducerSchedules } from '../../../api/producerDashboardApi';
import {
  createProducerSchedule,
  updateProducerSchedule,
  deleteProducerSchedule,
} from '../../../api/scheduleApi';
import { normalizeScheduleColor } from '../../../shared/schedule/scheduleColors';

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function monthRange(year, month) {
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);
  const start = new Date(monthStart);
  start.setDate(monthStart.getDate() - monthStart.getDay());
  const end = new Date(monthEnd);
  end.setDate(monthEnd.getDate() + (6 - monthEnd.getDay()));
  return { startDate: toDateStr(start), endDate: toDateStr(end) };
}

function mapSchedule(s, colorOverrides = {}) {
  const start = new Date(s.startAt);
  const resolvedColor = s.color || s.eventColor || (s.id != null ? colorOverrides[String(s.id)] : null);
  return {
    ...s,
    color: normalizeScheduleColor(resolvedColor),
    date: toDateStr(start),
    time: `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`,
  };
}

export default function ProducerSchedulePage() {
  const [schedules, setSchedules] = useState([]);
  const [upcomingSchedules, setUpcomingSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [addInitialDate, setAddInitialDate] = useState('');
  const [editEvent, setEditEvent] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [dayModal, setDayModal] = useState(null);
  const colorOverridesRef = useRef({});

  const applyLocalColorFallback = useCallback((list, fallbackPayload) => {
    if (!fallbackPayload) return list;
    const fallbackKey = `${fallbackPayload.title}__${fallbackPayload.startAt}__${fallbackPayload.endAt || ''}__${fallbackPayload.location || ''}`;
    let matched = false;

    return list.map((item) => {
      if (matched || item.color || item.eventColor) return item;
      const itemKey = `${item.title}__${item.startAt}__${item.endAt || ''}__${item.location || ''}`;
      if (itemKey !== fallbackKey) return item;
      matched = true;
      return { ...item, color: normalizeScheduleColor(fallbackPayload.color) };
    });
  }, []);

  const fetchSchedules = useCallback(async (year, month, fallbackPayload = null) => {
    setIsLoading(true);
    try {
      const { startDate, endDate } = monthRange(year, month);
      const data = await getProducerSchedules(startDate, endDate);
      const mapped = data.map((item) => mapSchedule(item, colorOverridesRef.current));
      setSchedules(applyLocalColorFallback(mapped, fallbackPayload));
    } catch {
      // 에러 시 캘린더 빈 상태 유지
    } finally {
      setIsLoading(false);
    }
  }, [applyLocalColorFallback]);

  const fetchUpcomingSchedules = useCallback(async () => {
    try {
      const today = new Date();
      const end = new Date(today);
      end.setDate(end.getDate() + 7);
      const data = await getProducerSchedules(toDateStr(today), toDateStr(end));
      const mapped = data.map((item) => mapSchedule(item, colorOverridesRef.current));
      setUpcomingSchedules(mapped);
    } catch {
      setUpcomingSchedules([]);
    }
  }, []);

  const handleMonthChange = useCallback((year, month) => {
    fetchSchedules(year, month);
  }, [fetchSchedules]);

  const handleAddEvent = async (payload) => {
    try {
      const created = await createProducerSchedule(payload);
      if (created?.id != null) {
        colorOverridesRef.current[String(created.id)] = payload.color;
      }
      const start = new Date(payload.startAt);
      await fetchSchedules(start.getFullYear(), start.getMonth(), payload);
      await fetchUpcomingSchedules();
      setAddModal(false);
      setAddInitialDate('');
    } catch {
      alert('일정 추가에 실패했습니다. 다시 시도해 주세요.');
    }
  };

  const handleEditEvent = async (payload) => {
    try {
      if (editEvent?.id != null) {
        colorOverridesRef.current[String(editEvent.id)] = payload.color;
      }
      await updateProducerSchedule(editEvent.id, payload);
      const start = new Date(payload.startAt);
      await fetchSchedules(start.getFullYear(), start.getMonth());
      await fetchUpcomingSchedules();
      setEditEvent(null);
    } catch {
      alert('일정 수정에 실패했습니다. 다시 시도해 주세요.');
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      await deleteProducerSchedule(id);
      setSchedules(prev => prev.filter(s => s.id !== id));
      setUpcomingSchedules(prev => prev.filter(s => s.id !== id));
    } catch {
      // TODO: 에러 토스트
    }
    setDetailEvent(null);
    if (dayModal) {
      const remaining = dayModal.events.filter(e => e.id !== id);
      if (remaining.length === 0) setDayModal(null);
      else setDayModal(prev => ({ ...prev, events: remaining }));
    }
  };

  const handleDayClick = (date, dayEvents) => {
    if (dayEvents.length === 0) {
      setAddInitialDate(toDateStr(date));
      setAddModal(true);
    } else {
      setDayModal({ date, events: dayEvents });
    }
  };

  useEffect(() => {
    fetchUpcomingSchedules();
  }, [fetchUpcomingSchedules]);

  return (
    <>
      <ProducerContentContainer>
        <div className={styles.headerWrap}>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <h1 className={styles.pageTitle}>일정 관리</h1>
              <span className={styles.subtitle}>오디션, 미팅, 촬영 일정을 한눈에 확인하고 관리하세요.</span>
            </div>
            <button onClick={() => { setAddInitialDate(''); setAddModal(true); }} className={styles.addBtn}>
              <span className={styles.addBtnIcon}>+</span>
              <span className={styles.addBtnText}>새 일정 추가</span>
            </button>
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.calendarArea}>
            <CalendarGrid
              events={schedules}
              isLoading={isLoading}
              onEventClick={(ev) => setDetailEvent(ev)}
              onDayClick={handleDayClick}
              onMonthChange={handleMonthChange}
            />
            <UpcomingPanel events={upcomingSchedules} onEventClick={(ev) => setDetailEvent(ev)} maxItems={7} />
          </div>
        </div>
      </ProducerContentContainer>

      {addModal && (
        <AddEventModal
          initialDate={addInitialDate}
          onClose={() => { setAddModal(false); setAddInitialDate(''); }}
          onSave={handleAddEvent}
        />
      )}
      {editEvent && (
        <AddEventModal
          initialData={editEvent}
          onClose={() => setEditEvent(null)}
          onSave={handleEditEvent}
        />
      )}
      {detailEvent && (
        <EventDetailModal
          event={detailEvent}
          onClose={() => setDetailEvent(null)}
          onDelete={() => handleDeleteEvent(detailEvent.id)}
          onEdit={() => { setEditEvent(detailEvent); setDetailEvent(null); }}
        />
      )}
      {dayModal && (
        <DayEventsModal
          date={dayModal.date}
          events={dayModal.events}
          onClose={() => setDayModal(null)}
          onDelete={(id) => handleDeleteEvent(id)}
          onEdit={(ev) => { setEditEvent(ev); setDayModal(null); }}
        />
      )}
    </>
  );
}
